import { createSlice } from '@reduxjs/toolkit';
import userService from '../../utils/userService';

const initialState = {
  users: [],
  professionals: [],
  organizations: [],
  selectedUser: null,
  userProfile: null,
  userActivity: [],
  userStats: null,
  userFavorites: [],
  userReviews: [],
  searchResults: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    location: '',
    skills: [],
    rating: 0,
    verified: false
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUsers: (state, action) => {
      state.users = action.payload.users || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setProfessionals: (state, action) => {
      state.professionals = action.payload.users || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setOrganizations: (state, action) => {
      state.organizations = action.payload.users || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserProfile: (state, action) => {
      state.userProfile = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserActivity: (state, action) => {
      state.userActivity = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserStats: (state, action) => {
      state.userStats = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserFavorites: (state, action) => {
      state.userFavorites = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserReviews: (state, action) => {
      state.userReviews = action.payload;
      state.loading = false;
      state.error = null;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload.users || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    addUser: (state, action) => {
      state.users.unshift(action.payload);
      if (action.payload.type === 'professional') {
        state.professionals.unshift(action.payload);
      } else if (action.payload.type === 'organization') {
        state.organizations.unshift(action.payload);
      }
      state.loading = false;
      state.error = null;
    },
    updateUser: (state, action) => {
      const updatedUser = action.payload;
      
      // Mettre à jour dans la liste générale
      const userIndex = state.users.findIndex(user => user._id === updatedUser._id);
      if (userIndex !== -1) {
        state.users[userIndex] = updatedUser;
      }
      
      // Mettre à jour dans la liste spécifique
      if (updatedUser.type === 'professional') {
        const profIndex = state.professionals.findIndex(user => user._id === updatedUser._id);
        if (profIndex !== -1) {
          state.professionals[profIndex] = updatedUser;
        }
      } else if (updatedUser.type === 'organization') {
        const orgIndex = state.organizations.findIndex(user => user._id === updatedUser._id);
        if (orgIndex !== -1) {
          state.organizations[orgIndex] = updatedUser;
        }
      }
      
      // Mettre à jour l'utilisateur sélectionné si c'est le même
      if (state.selectedUser && state.selectedUser._id === updatedUser._id) {
        state.selectedUser = updatedUser;
      }
      
      state.loading = false;
      state.error = null;
    },
    removeUser: (state, action) => {
      const userId = action.payload;
      
      state.users = state.users.filter(user => user._id !== userId);
      state.professionals = state.professionals.filter(user => user._id !== userId);
      state.organizations = state.organizations.filter(user => user._id !== userId);
      
      if (state.selectedUser && state.selectedUser._id === userId) {
        state.selectedUser = null;
      }
      
      state.loading = false;
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        location: '',
        skills: [],
        rating: 0,
        verified: false
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    toggleUserFavorite: (state, action) => {
      const userId = action.payload;
      const index = state.userFavorites.findIndex(id => id === userId);
      if (index !== -1) {
        state.userFavorites.splice(index, 1);
      } else {
        state.userFavorites.push(userId);
      }
    }
  }
});

// Actions asynchrones
export const fetchUsers = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await userService.getUsers(params);
    dispatch(setUsers(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchUserById = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const user = await userService.getUserById(id);
    dispatch(setSelectedUser(user));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const createUser = (userData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const newUser = await userService.createUser(userData);
    dispatch(addUser(newUser));
    return newUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const updateUserById = (id, userData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedUser = await userService.updateUser(id, userData);
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteUserById = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await userService.deleteUser(id);
    dispatch(removeUser(id));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const uploadUserAvatar = (id, formData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await userService.uploadAvatar(id, formData);
    // Rafraîchir l'utilisateur pour afficher le nouvel avatar
    dispatch(fetchUserById(id));
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchUserStats = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const stats = await userService.getUserStats(id);
    dispatch(setUserStats(stats));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const searchUsersByLocation = (params) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await userService.searchByLocation(params);
    dispatch(setSearchResults(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const blockUser = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedUser = await userService.blockUser(id);
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const unblockUser = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedUser = await userService.unblockUser(id);
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const verifyUser = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedUser = await userService.verifyUser(id);
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const unverifyUser = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedUser = await userService.unverifyUser(id);
    dispatch(updateUser(updatedUser));
    return updatedUser;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchUserActivity = (id, params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const activity = await userService.getUserActivity(id, params);
    dispatch(setUserActivity(activity));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchUserProfile = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const profile = await userService.getUserProfile(id);
    dispatch(setUserProfile(profile));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const searchUsers = (query, params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await userService.searchUsers(query, params);
    dispatch(setSearchResults(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchUserFavorites = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const favorites = await userService.getUserFavorites(id);
    dispatch(setUserFavorites(favorites));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const addUserFavorite = (id, favoriteId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await userService.addUserFavorite(id, favoriteId);
    dispatch(toggleUserFavorite(favoriteId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const removeUserFavorite = (id, favoriteId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await userService.removeUserFavorite(id, favoriteId);
    dispatch(toggleUserFavorite(favoriteId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchUserReviews = (id) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const reviews = await userService.getUserReviews(id);
    dispatch(setUserReviews(reviews));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const addUserReview = (id, reviewData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const review = await userService.addUserReview(id, reviewData);
    // Rafraîchir les avis de l'utilisateur
    dispatch(fetchUserReviews(id));
    return review;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

// Sélecteurs
export const selectUsers = (state) => state.users.users;
export const selectProfessionals = (state) => state.users.professionals;
export const selectOrganizations = (state) => state.users.organizations;
export const selectSelectedUser = (state) => state.users.selectedUser;
export const selectUserProfile = (state) => state.users.userProfile;
export const selectUserActivity = (state) => state.users.userActivity;
export const selectUserStats = (state) => state.users.userStats;
export const selectUserFavorites = (state) => state.users.userFavorites;
export const selectUserReviews = (state) => state.users.userReviews;
export const selectSearchResults = (state) => state.users.searchResults;
export const selectUsersLoading = (state) => state.users.loading;
export const selectUsersError = (state) => state.users.error;
export const selectUsersFilters = (state) => state.users.filters;
export const selectUsersPagination = (state) => state.users.pagination;

export const selectUserById = (state, userId) => {
  return state.users.users.find(user => user._id === userId);
};

export const selectFilteredUsers = (state) => {
  const { users, filters } = state.users;
  
  return users.filter(user => {
    // Filtrage par recherche textuelle
    if (filters.search && 
        !user.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.email?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.bio?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    // Filtrage par localisation
    if (filters.location && !user.location?.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    // Filtrage par compétences
    if (filters.skills.length > 0) {
      const userSkills = user.skills || [];
      const hasMatchingSkill = filters.skills.some(skill => 
        userSkills.some(userSkill => userSkill.toLowerCase().includes(skill.toLowerCase()))
      );
      if (!hasMatchingSkill) {
        return false;
      }
    }
    
    // Filtrage par note
    if (filters.rating > 0 && (user.rating || 0) < filters.rating) {
      return false;
    }
    
    // Filtrage par vérification
    if (filters.verified && !user.verified) {
      return false;
    }
    
    return true;
  });
};

export const selectFilteredProfessionals = (state) => {
  const { professionals, filters } = state.users;
  
  return professionals.filter(user => {
    // Appliquer les mêmes filtres que pour tous les utilisateurs
    if (filters.search && 
        !user.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.email?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.bio?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    if (filters.location && !user.location?.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    if (filters.skills.length > 0) {
      const userSkills = user.skills || [];
      const hasMatchingSkill = filters.skills.some(skill => 
        userSkills.some(userSkill => userSkill.toLowerCase().includes(skill.toLowerCase()))
      );
      if (!hasMatchingSkill) {
        return false;
      }
    }
    
    if (filters.rating > 0 && (user.rating || 0) < filters.rating) {
      return false;
    }
    
    if (filters.verified && !user.verified) {
      return false;
    }
    
    return true;
  });
};

export const selectFilteredOrganizations = (state) => {
  const { organizations, filters } = state.users;
  
  return organizations.filter(user => {
    // Appliquer les mêmes filtres que pour tous les utilisateurs
    if (filters.search && 
        !user.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.email?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !user.description?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    if (filters.location && !user.location?.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    if (filters.rating > 0 && (user.rating || 0) < filters.rating) {
      return false;
    }
    
    if (filters.verified && !user.verified) {
      return false;
    }
    
    return true;
  });
};

export const {
  setLoading,
  setError,
  clearError,
  setUsers,
  setProfessionals,
  setOrganizations,
  setSelectedUser,
  setUserProfile,
  setUserActivity,
  setUserStats,
  setUserFavorites,
  setUserReviews,
  setSearchResults,
  addUser,
  updateUser,
  removeUser,
  setFilters,
  clearFilters,
  setPagination,
  toggleUserFavorite
} = usersSlice.actions;

export default usersSlice.reducer;